import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ErrorsHostDirective } from '@app/core/forms/directives/errors-host.directive';
import { IfInvalidDirective } from '@app/core/forms/directives/if-invalid.directive';
import { ScrollElements } from '@app/core/forms/directives/scroll-ements';
import { ScrollToInvalidFieldOnFormSubmitDirective } from '@app/core/forms/directives/scroll-to-invalid-field-on-form-submit.directive';
import { applyErrorsToForm } from '@app/core/helpers/apply-errors-to-form';
import { extractHttpErrorResponse } from '@app/core/helpers/extract-http-error-response';
import { NotificationsService } from '@app/core/notifications/services/notifications.service';
import { ForgotPassword } from '@app/store';
import {
  ActionCompletion,
  Actions,
  ofActionCompleted,
  Store,
} from '@ngxs/store';
import { tap } from 'rxjs';

@Component({
  selector: 'app-forgot-password-page',
  templateUrl: './forgot-password.page.html',
  styleUrls: ['./forgot-password.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    ScrollToInvalidFieldOnFormSubmitDirective,
    IfInvalidDirective,
    ErrorsHostDirective,
  ],
})
export class ForgotPasswordPage implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private actions$: Actions,
    private destroyRef: DestroyRef,
    private notificationsService: NotificationsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  protected form!: FormGroup;

  protected ScrollElements = ScrollElements;

  @ViewChild(ScrollToInvalidFieldOnFormSubmitDirective)
  private scrollDirective!: ScrollToInvalidFieldOnFormSubmitDirective;

  ngOnInit(): void {
    this.form = this.buildForm();
    this.handleBackendResponse();
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      email: [null, Validators.required],
    });
  }

  private handleBackendResponse(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(ForgotPassword),
        tap((actionCompletion: ActionCompletion) => {
          if (actionCompletion.result.successful) {
            this.notificationsService.displaySuccessNotification(
              'Password Reset Requested!',
              'We have sent you a link to reset your password. Please check your e-mail.',
            );
          } else {
            const errors = extractHttpErrorResponse(actionCompletion);
            applyErrorsToForm(errors, this.form);
            this.scrollDirective.scrollToFirstInvalidControl();
          }
        }),
      )
      .subscribe();
  }

  get emailFC(): FormControl {
    return this.form.get('email') as FormControl;
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }

    this.store.dispatch(new ForgotPassword(this.form.value));
  }
}
